import { Component, OnInit, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { Router } from '@angular/router';
import { Order } from '../../core/models/product.models';
import { OrdersService } from '../../core/services/orders.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';
import { CurrentUserService } from '../../core/auth/current-user.service';

@Component({
  selector: 'app-billing',
  imports: [FormsModule, DatePipe],
  templateUrl: './billing.html',
  styleUrl: './billing.scss',
})
export class Billing implements OnInit {
  private readonly ordersService = inject(OrdersService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly router = inject(Router);
  get canUpdateOrders(): boolean { return this.currentUser.hasPermission('ORDER_STATUS_UPDATE') || this.currentUser.hasPermission('ORDER_UPDATE'); }

  readonly statuses = ['Created', 'Confirmed', 'Paid', 'Shipped', 'Delivered', 'Cancelled'];
  orders: Order[] = [];
  selectedStatuses: Record<string, string> = {};
  isLoading = false;
  busyOrderId = '';
  errorMessage = '';
  successMessage = '';
  datePreset: 'today' | '7days' | 'month' | 'custom' | 'all' = 'today';
  fromDate = '';
  toDate = '';

  ngOnInit(): void {
    if (this.isInternalSales) this.applyDatePreset('today');
    this.loadOrders();
  }

  get isInternalSales(): boolean { return this.router.url.startsWith('/business/internal-sales'); }

  get filteredOrders(): Order[] {
    if (!this.isInternalSales || this.datePreset === 'all') return this.orders;
    const from = this.fromDate ? new Date(`${this.fromDate}T00:00:00`) : null;
    const to = this.toDate ? new Date(`${this.toDate}T23:59:59.999`) : null;
    return this.orders.filter((order) => {
      if (!order.createdAt) return false;
      const created = new Date(order.createdAt);
      return (!from || created >= from) && (!to || created <= to);
    });
  }

  get filteredRevenue(): number { return this.filteredOrders.reduce((sum, order) => sum + Number(order.totalAmount || 0), 0); }
  get filteredUnits(): number { return this.filteredOrders.reduce((sum, order) => sum + Number(order.quantity || 0), 0); }

  applyDatePreset(preset: 'today' | '7days' | 'month' | 'custom' | 'all'): void {
    this.datePreset = preset;
    if (preset === 'custom' || preset === 'all') {
      if (preset === 'all') { this.fromDate = ''; this.toDate = ''; }
      return;
    }
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (preset === '7days') start.setDate(start.getDate() - 6);
    if (preset === 'month') start.setDate(1);
    this.fromDate = this.localDate(start);
    this.toDate = this.localDate(today);
  }

  onCustomDateChange(): void { this.datePreset = 'custom'; }

  loadOrders(): void {
    this.isLoading = true;
    this.errorMessage = '';
    const request = this.isInternalSales ? this.ordersService.sales() : this.ordersService.list();
    request.pipe(finalize(() => (this.isLoading = false))).subscribe({
      next: (orders) => {
        this.orders = orders;
        this.selectedStatuses = Object.fromEntries(orders.map((order) => [order.id, this.statusName(order.status)]));
        this.isLoading = false;
      },
      error: (error: unknown) => {
        this.isLoading = false;
        this.errorMessage = httpErrorMessage(error, 'Unable to load orders.');
      },
    });
  }

  updateStatus(order: Order): void {
    const status = this.selectedStatuses[order.id];
    if (!status || status === this.statusName(order.status)) return;

    this.busyOrderId = order.id;
    this.clearMessages();
    this.ordersService.updateStatus(order.id, status).pipe(finalize(() => (this.busyOrderId = ''))).subscribe({
      next: (updated) => {
        this.orders = this.orders.map((item) => item.id === order.id ? updated : item);
        this.selectedStatuses[order.id] = this.statusName(updated.status);
        this.successMessage = `Order status changed to ${this.statusName(updated.status)}.`;
      },
      error: (error: unknown) => {
        this.selectedStatuses[order.id] = this.statusName(order.status);
        this.errorMessage = httpErrorMessage(error, 'Unable to update order status.');
      },
    });
  }

  openVoucher(order: Order): void {
    const voucherTab = window.open('about:blank', '_blank');
    if (voucherTab) voucherTab.opener = null;
    this.busyOrderId = order.id;
    this.clearMessages();
    this.ordersService.purchaseVoucher(order.id).pipe(finalize(() => (this.busyOrderId = ''))).subscribe({
      next: (voucher) => {
        this.orders = this.orders.map((item) => item.id === order.id ? { ...item, purchaseVoucher: voucher } : item);
        if (voucherTab) voucherTab.location.href = voucher.url;
        else window.location.href = voucher.url;
        this.successMessage = `Purchase voucher ${voucher.number} is ready.`;
      },
      error: (error: unknown) => {
        voucherTab?.close();
        this.errorMessage = httpErrorMessage(error, 'Unable to generate purchase voucher.');
      },
    });
  }

  openInvoice(order: Order): void {
    if (order.saleInvoice?.url) { window.open(order.saleInvoice.url, '_blank', 'noopener'); return; }
    const invoiceTab = window.open('about:blank', '_blank');
    if (invoiceTab) invoiceTab.opener = null;
    this.busyOrderId = order.id;
    this.clearMessages();
    this.ordersService.saleInvoice(order.id).pipe(finalize(() => (this.busyOrderId = ''))).subscribe({
      next: (invoice) => {
        this.orders = this.orders.map((item) => item.id === order.id ? { ...item, saleInvoice: invoice } : item);
        if (invoiceTab) invoiceTab.location.href = invoice.url;
        else window.location.href = invoice.url;
        this.successMessage = `Sale invoice ${invoice.number} is ready.`;
      },
      error: (error: unknown) => {
        invoiceTab?.close();
        this.errorMessage = httpErrorMessage(error, 'Unable to open sale invoice.');
      },
    });
  }

  statusName(status: string | number): string {
    if (typeof status === 'string') return status;
    return ({ 1: 'Created', 2: 'Confirmed', 3: 'Paid', 4: 'Shipped', 5: 'Delivered', 6: 'Cancelled' } as Record<number, string>)[status] ?? 'Created';
  }

  formatAmount(value: number, currency = 'INR'): string {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 }).format(value ?? 0);
  }

  private clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }

  private localDate(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
